import { useState } from 'react';
import { CURRENCIES } from '../utils/constants';
import { HelpTip } from './HelpTip';
import { TIPS } from '../help/content';
import { ChevronDown, ChevronUp, Copy, Eye, EyeOff, RefreshCw, Trash2 } from 'lucide-react';

const MCP_SERVER_URL  = 'https://mcp.myspend.info/mcp';
const CLAUDE_CONFIG   = `"mcpServers": {
  "myspend": {
    "url": "${MCP_SERVER_URL}"
  }
}`;
const DOCKER_CMD = (token) => `docker run -i --rm \\
  -e MYSPEND_API_KEY=${token || '<your-token>'} \\
  sunvirgujral/myspend-mcp`;

function PrefsModal({ defaultCurrency, adjustmentFactor, onSave, onClose, account, onGenerateMcpToken, onRevokeMcpToken }) {
  const [currency,          setCurrency]          = useState(defaultCurrency);
  const [adjustment,        setAdjustment]        = useState(String(adjustmentFactor ?? 0));
  const [showToken,         setShowToken]         = useState(false);
  const [mcpBusy,           setMcpBusy]           = useState(false);
  const [copied,            setCopied]            = useState(null);
  const [showOAuthSetup,    setShowOAuthSetup]    = useState(false);
  const [showDockerSection, setShowDockerSection] = useState(false);

  const mcpToken = account?.mcpApiToken ?? null;

  const handleCopy = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(key);
      setTimeout(() => setCopied(null), 1500);
    });
  };

  const handleGenerate = async () => {
    setMcpBusy(true);
    try { await onGenerateMcpToken(); } finally { setMcpBusy(false); }
  };

  const handleRevoke = async () => {
    setMcpBusy(true);
    try { await onRevokeMcpToken(); } finally { setMcpBusy(false); }
  };

  const adjNum    = parseFloat(adjustment) || 0;
  const multiplier = (1 + adjNum / 100).toFixed(4);

  const handleSave = () => {
    onSave({ defaultCurrency: currency, adjustmentFactor: parseFloat(adjustment) || 0 });
    onClose();
  };

  return (
    <div
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", display: "flex", alignItems: "flex-end", justifyContent: "center", zIndex: 1000, backdropFilter: "blur(4px)" }}
      onClick={onClose}
    >
      <div
        style={{ background: "#fff", borderRadius: "20px 20px 0 0", padding: "24px 20px", width: "100%", maxWidth: 390, animation: "slideUp 0.3s ease-out", maxHeight: "90vh", overflowY: "auto" }}
        onClick={e => e.stopPropagation()}
      >
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 20 }}>
          <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: "#111" }}>Preferences</h2>
          <button onClick={onClose} style={{ background: "none", border: "none", fontSize: 24, cursor: "pointer", color: "#888", lineHeight: 1 }}>×</button>
        </div>

        {/* Default Currency */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", letterSpacing: 0.8, textTransform: "uppercase", display: "flex", alignItems: "center", gap: 4, marginBottom: 8 }}>
            Default Report Currency <HelpTip content={TIPS.currency} placement="top" />
          </label>
          <select
            value={currency}
            onChange={e => setCurrency(e.target.value)}
            style={{ width: "100%", padding: "12px", borderRadius: 10, border: "1.5px solid #E5E7EB", fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit", background: "#fff" }}
          >
            {CURRENCIES.map(c => (
              <option key={c.code} value={c.code}>{c.code} — {c.name}</option>
            ))}
          </select>
          <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 6 }}>
            All new and updated expenses will be converted to this currency.
          </div>
        </div>

        {/* Adjustment Factor */}
        <div style={{ marginBottom: 24 }}>
          <label style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", letterSpacing: 0.8, textTransform: "uppercase", display: "flex", alignItems: "center", gap: 4, marginBottom: 8 }}>
            Adjustment Factor <HelpTip content={TIPS.adjustmentFactor} placement="top" />
          </label>
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ position: "relative", flex: 1 }}>
              <input
                type="number"
                value={adjustment}
                onChange={e => setAdjustment(e.target.value)}
                step="0.1"
                placeholder="0"
                style={{ width: "100%", padding: "12px 36px 12px 12px", borderRadius: 10, border: "1.5px solid #E5E7EB", fontSize: 14, outline: "none", boxSizing: "border-box", fontFamily: "inherit" }}
              />
              <span style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)", fontSize: 14, color: "#6B7280", pointerEvents: "none" }}>%</span>
            </div>
            <div style={{ fontSize: 13, color: "#6B7280", whiteSpace: "nowrap" }}>
              = ×{multiplier}
            </div>
          </div>
          <div style={{ fontSize: 12, color: "#9CA3AF", marginTop: 6 }}>
            Applied on top of the exchange rate. Use positive values for a markup (e.g. 5 for +5%), negative for a discount.
          </div>
        </div>

        {/* Preview */}
        <div style={{ background: "#F0F4FF", borderRadius: 10, padding: "12px 14px", marginBottom: 20, fontSize: 13, color: "#0A7C8A" }}>
          Example: 100.00 EUR → <strong>{(100 * (usdEquiv(currency) || 1) * (1 + adjNum / 100)).toFixed(2)} {currency}</strong>
          {adjNum !== 0 && <span style={{ color: "#9CA3AF" }}> (incl. {adjNum > 0 ? "+" : ""}{adjNum}% adj.)</span>}
        </div>

        {/* AI Agent Integration */}
        {onGenerateMcpToken && (
          <div style={{ marginBottom: 24 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#6B7280", letterSpacing: 0.8, textTransform: "uppercase", marginBottom: 10 }}>
              AI Agent Integration (MCP)
            </div>

            {/* ── OAuth / Remote method ── */}
            <div style={{ border: "1.5px solid #99F6E4", borderRadius: 12, marginBottom: 10, overflow: "hidden" }}>
              <div style={{ background: "#F0FDFA", padding: "12px 14px" }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#0F766E" }}>Claude, Cursor, Codex & more</span>
                  <span style={{ fontSize: 10, background: "#99F6E4", color: "#0F766E", borderRadius: 4, padding: "2px 7px", fontWeight: 800, letterSpacing: 0.3 }}>RECOMMENDED</span>
                </div>
                <div style={{ fontSize: 12, color: "#374151", lineHeight: 1.5, marginBottom: 10 }}>
                  Add the server URL to any MCP-compatible client. Sign-in happens automatically — no token required.
                </div>

                {/* Server URL row */}
                <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                  <div style={{ flex: 1, background: "#fff", border: "1px solid #CCFBF1", borderRadius: 8, padding: "8px 10px", fontSize: 11, color: "#0F766E", fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {MCP_SERVER_URL}
                  </div>
                  <button
                    onClick={() => handleCopy(MCP_SERVER_URL, 'url')}
                    style={{ flexShrink: 0, padding: "8px", border: "1px solid #CCFBF1", borderRadius: 8, background: "#fff", cursor: "pointer", color: copied === 'url' ? "#0F766E" : "#6B7280", display: "flex" }}
                    title="Copy server URL"
                  >
                    <Copy size={14} />
                  </button>
                </div>
              </div>

              {/* Setup instructions toggle */}
              <button
                onClick={() => setShowOAuthSetup(s => !s)}
                style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 14px", background: showOAuthSetup ? "#E6FFFA" : "#F0FDFA", border: "none", borderTop: "1px solid #CCFBF1", cursor: "pointer", fontSize: 12, fontWeight: 600, color: "#0F766E" }}
              >
                <span>How to connect</span>
                {showOAuthSetup ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>

              {showOAuthSetup && (
                <div style={{ padding: "14px", background: "#fff", borderTop: "1px solid #CCFBF1" }}>

                  {/* Claude Desktop */}
                  <div style={{ marginBottom: 14 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 6 }}>Claude Desktop</div>
                    <div style={{ fontSize: 11, color: "#6B7280", marginBottom: 6, lineHeight: 1.4 }}>
                      Add to <code style={{ background: "#F3F4F6", padding: "1px 4px", borderRadius: 3 }}>claude_desktop_config.json</code>:
                    </div>
                    <div style={{ position: "relative" }}>
                      <pre style={{ margin: 0, background: "#111827", borderRadius: 8, padding: "10px 12px", fontSize: 10, color: "#D1FAE5", fontFamily: "monospace", lineHeight: 1.6, overflowX: "auto" }}>{CLAUDE_CONFIG}</pre>
                      <button
                        onClick={() => handleCopy(`{\n  ${CLAUDE_CONFIG}\n}`, 'claude')}
                        style={{ position: "absolute", top: 6, right: 6, padding: "4px 6px", background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 6, cursor: "pointer", color: copied === 'claude' ? "#6EE7B7" : "#9CA3AF", display: "flex" }}
                        title="Copy"
                      >
                        <Copy size={11} />
                      </button>
                    </div>
                    <div style={{ fontSize: 11, color: "#9CA3AF", marginTop: 6, lineHeight: 1.4 }}>
                      Config file location: <code style={{ background: "#F3F4F6", padding: "1px 4px", borderRadius: 3, fontSize: 10 }}>~/Library/Application Support/Claude/</code>
                    </div>
                  </div>

                  {/* Other clients */}
                  <div style={{ borderTop: "1px solid #F3F4F6", paddingTop: 12 }}>
                    <div style={{ fontSize: 11, fontWeight: 700, color: "#374151", marginBottom: 8 }}>Cursor / Windsurf / Zed / Codex</div>
                    <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
                      {[
                        { name: "Cursor", path: "Settings → MCP → Add Server → paste URL" },
                        { name: "Windsurf", path: "Settings → Cascade → MCP → Add Server" },
                        { name: "Zed", path: "Settings (JSON) → context_servers → add URL" },
                        { name: "Codex CLI", path: "codex --mcp-server " + MCP_SERVER_URL },
                      ].map(c => (
                        <div key={c.name} style={{ display: "flex", gap: 8, alignItems: "baseline" }}>
                          <span style={{ fontSize: 11, fontWeight: 600, color: "#374151", minWidth: 64 }}>{c.name}</span>
                          <span style={{ fontSize: 11, color: "#6B7280", lineHeight: 1.4 }}>{c.path}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                </div>
              )}
            </div>

            {/* ── Docker / CLI method ── */}
            <div style={{ border: "1.5px solid #E5E7EB", borderRadius: 12, overflow: "hidden" }}>
              <button
                onClick={() => setShowDockerSection(s => !s)}
                style={{ width: "100%", display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", background: "#F9FAFB", border: "none", cursor: "pointer" }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <span style={{ fontSize: 13, fontWeight: 700, color: "#374151" }}>Docker / CLI</span>
                  <span style={{ fontSize: 10, background: "#F3F4F6", color: "#6B7280", borderRadius: 4, padding: "2px 7px", fontWeight: 700 }}>API TOKEN</span>
                </div>
                {showDockerSection ? <ChevronUp size={14} color="#9CA3AF" /> : <ChevronDown size={14} color="#9CA3AF" />}
              </button>

              {showDockerSection && (
                <div style={{ padding: "14px", background: "#fff", borderTop: "1px solid #E5E7EB" }}>
                  <div style={{ fontSize: 12, color: "#6B7280", lineHeight: 1.5, marginBottom: 12 }}>
                    For the <code style={{ background: "#F3F4F6", padding: "1px 4px", borderRadius: 3 }}>myspend-mcp</code> Docker image (stdio transport). Requires an API token below.
                  </div>

                  {/* API Token */}
                  <div style={{ marginBottom: 10 }}>
                    <div style={{ fontSize: 11, fontWeight: 600, color: "#9CA3AF", marginBottom: 6 }}>API Token</div>
                    {mcpToken ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 8 }}>
                        <div style={{ flex: 1, background: "#F9FAFB", border: "1px solid #E5E7EB", borderRadius: 8, padding: "8px 10px", fontSize: 11, color: "#374151", fontFamily: "monospace", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                          {showToken ? mcpToken : '•'.repeat(20)}
                        </div>
                        <button onClick={() => setShowToken(s => !s)} style={{ flexShrink: 0, padding: "8px", border: "1px solid #E5E7EB", borderRadius: 8, background: "#fff", cursor: "pointer", color: "#6B7280", display: "flex" }}>
                          {showToken ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                        <button onClick={() => handleCopy(mcpToken, 'token')} style={{ flexShrink: 0, padding: "8px", border: "1px solid #E5E7EB", borderRadius: 8, background: "#fff", cursor: "pointer", color: copied === 'token' ? "#0A7C8A" : "#6B7280", display: "flex" }}>
                          <Copy size={14} />
                        </button>
                      </div>
                    ) : (
                      <div style={{ fontSize: 12, color: "#9CA3AF", fontStyle: "italic", marginBottom: 8 }}>No token generated yet</div>
                    )}

                    <div style={{ display: "flex", gap: 8, marginBottom: mcpToken ? 12 : 0 }}>
                      <button
                        onClick={handleGenerate}
                        disabled={mcpBusy}
                        style={{ flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 5, padding: "9px 12px", borderRadius: 8, border: "none", background: "#0A7C8A", color: "#fff", fontSize: 12, fontWeight: 700, cursor: mcpBusy ? "default" : "pointer", opacity: mcpBusy ? 0.6 : 1 }}
                      >
                        <RefreshCw size={13} />
                        {mcpToken ? 'Regenerate' : 'Generate Token'}
                      </button>
                      {mcpToken && (
                        <button
                          onClick={handleRevoke}
                          disabled={mcpBusy}
                          style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 5, padding: "9px 12px", borderRadius: 8, border: "1.5px solid #FCA5A5", background: "#FFF5F5", color: "#DC2626", fontSize: 12, fontWeight: 700, cursor: mcpBusy ? "default" : "pointer", opacity: mcpBusy ? 0.6 : 1 }}
                        >
                          <Trash2 size={13} />
                          Revoke
                        </button>
                      )}
                    </div>
                  </div>

                  {/* Docker command */}
                  {mcpToken && (
                    <div>
                      <div style={{ fontSize: 11, fontWeight: 600, color: "#9CA3AF", marginBottom: 6 }}>Docker command</div>
                      <div style={{ position: "relative" }}>
                        <pre style={{ margin: 0, background: "#111827", borderRadius: 8, padding: "10px 12px", fontSize: 10, color: "#E5E7EB", fontFamily: "monospace", lineHeight: 1.6, overflowX: "auto", whiteSpace: "pre-wrap", wordBreak: "break-all" }}>
                          {DOCKER_CMD(showToken ? mcpToken : mcpToken.slice(0, 8) + '…')}
                        </pre>
                        <button
                          onClick={() => handleCopy(DOCKER_CMD(mcpToken), 'docker')}
                          style={{ position: "absolute", top: 6, right: 6, padding: "4px 6px", background: "rgba(255,255,255,0.1)", border: "1px solid rgba(255,255,255,0.2)", borderRadius: 6, cursor: "pointer", color: copied === 'docker' ? "#6EE7B7" : "#9CA3AF", display: "flex" }}
                          title="Copy"
                        >
                          <Copy size={11} />
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        )}

        <div style={{ display: "flex", gap: 12 }}>
          <button onClick={onClose} style={{ flex: 1, padding: "14px", borderRadius: 12, border: "1.5px solid #E5E7EB", background: "#fff", fontSize: 14, fontWeight: 700, cursor: "pointer", color: "#6B7280" }}>
            Cancel
          </button>
          <button
            onClick={handleSave}
            style={{ flex: 2, padding: "14px", borderRadius: 12, border: "none", background: "linear-gradient(135deg, #0A7C8A 0%, #05C4B0 100%)", fontSize: 14, fontWeight: 700, cursor: "pointer", color: "#fff", boxShadow: "0 4px 15px rgba(5, 196, 176, 0.3)" }}
          >
            Save &amp; Reconvert All
          </button>
        </div>
      </div>
    </div>
  );
}

// Rough approximation for the preview only — real rates come from the API.
function usdEquiv(currency) {
  const approx = { USD: 1.0, EUR: 1.168, GBP: 1.27, JPY: 0.0067, CAD: 0.79, AUD: 0.70, CHF: 1.20, CNY: 0.14, INR: 0.013, MXN: 0.056, BRL: 0.20, SGD: 0.81, VND: 0.000043, KRW: 0.00079 };
  const eurUsd = approx["EUR"];
  const targetPerUsd = approx[currency] ? 1 / approx[currency] : 1;
  return eurUsd * targetPerUsd;
}

export default PrefsModal;
